import React, { useMemo, useState } from "react";
import { api } from "@/lib/api";
import TopNav from "@/components/TopNav";
import Dropzone from "@/components/Dropzone";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "@/hooks/use-toast";
import { Download, Wand2 } from "lucide-react";

export default function RestorePage() {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const [outUrl, setOutUrl] = useState(null);

  const inputUrl = useMemo(() => {
    if (!files[0]) return null;
    return URL.createObjectURL(files[0]);
  }, [files]);

  const canRun = useMemo(() => files.length === 1 && !busy, [files.length, busy]);

  const reset = () => {
    if (outUrl) URL.revokeObjectURL(outUrl);
    setOutUrl(null);
  };

  const run = async () => {
    if (!files[0]) return;
    reset();
    setBusy(true);
    setProgress(10);

    try {
      const form = new FormData();
      form.append("file", files[0]);

      const res = await api.post(`/restore-photo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
        onUploadProgress: (e) => {
          if (!e.total) return;
          const pct = Math.round((e.loaded / e.total) * 25);
          setProgress(Math.min(25, Math.max(10, pct)));
        },
      });

      const url = URL.createObjectURL(res.data);
      setOutUrl(url);
      setProgress(100);

      toast({
        title: "Restored",
        description: "Your photo has been enhanced. Download the restored PNG.",
      });
    } catch (e) {
      toast({
        title: "Error",
        description: e?.response?.data?.detail || "Restore failed",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
      setTimeout(() => setProgress(0), 900);
    }
  };

  const download = () => {
    if (!outUrl) return;
    const a = document.createElement("a");
    a.href = outUrl;
    a.download = "restored.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="min-h-screen bg-[#07070a] text-zinc-100" data-testid="restore-page">
      <TopNav />
      <div
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background:
            "radial-gradient(900px 420px at 20% 0%, rgba(139,92,246,0.18), transparent 60%), radial-gradient(900px 420px at 90% 15%, rgba(14,165,233,0.10), transparent 55%), radial-gradient(900px 420px at 50% 100%, rgba(236,72,153,0.06), transparent 55%)",
        }}
      />

      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-5" data-testid="restore-left">
            <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl" data-testid="restore-title">
              Photo Restoration
            </h1>
            <p className="mt-3 text-base text-zinc-300 md:text-lg" data-testid="restore-subtitle">
              Improve faded, blurry or low-quality photos using denoise + sharpening + lighting + tint correction.
            </p>

            {progress > 0 ? (
              <div className="mt-6 space-y-2" data-testid="restore-progress-wrapper">
                <div className="flex items-center justify-between text-xs text-zinc-400">
                  <span data-testid="restore-progress-label">Processing</span>
                  <span data-testid="restore-progress-value">{progress}%</span>
                </div>
                <Progress value={progress} data-testid="restore-progress-bar" />
              </div>
            ) : null}

            <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="restore-actions">
              <Button
                onClick={run}
                disabled={!canRun}
                className="rounded-full bg-violet-500 text-white hover:bg-violet-400 disabled:opacity-50"
                data-testid="restore-run-button"
              >
                <Wand2 className="mr-2 h-4 w-4" />
                Restore
              </Button>

              <Button
                onClick={download}
                disabled={!outUrl}
                variant="secondary"
                className="rounded-full bg-white/5 text-zinc-100 ring-1 ring-white/10 hover:bg-white/10 disabled:opacity-50"
                data-testid="restore-download-button"
              >
                <Download className="mr-2 h-4 w-4" />
                Download PNG
              </Button>
            </div>
          </div>

          <div className="lg:col-span-7" data-testid="restore-right">
            <Card className="rounded-3xl border-white/10 bg-zinc-950/50" data-testid="restore-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base md:text-lg" data-testid="restore-card-title">
                  Upload
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <Dropzone mode="single" files={files} setFiles={setFiles} maxFiles={1} />

                <div className="grid gap-4 lg:grid-cols-2" data-testid="restore-previews">
                  <div className="space-y-2" data-testid="restore-input-preview">
                    <div className="text-sm font-medium text-zinc-200" data-testid="restore-input-title">
                      Input
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/40">
                      <div className="h-64">
                        {inputUrl ? (
                          <img
                            src={inputUrl}
                            alt="input"
                            className="h-full w-full object-contain"
                            data-testid="restore-input-image"
                          />
                        ) : (
                          <div className="grid h-full place-items-center text-sm text-zinc-400" data-testid="restore-input-empty">
                            Choose a file to preview
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2" data-testid="restore-output-preview">
                    <div className="text-sm font-medium text-zinc-200" data-testid="restore-output-title">
                      Output
                    </div>
                    <div className="overflow-hidden rounded-2xl border border-white/10 bg-zinc-950/40">
                      <div className="h-64">
                        {outUrl ? (
                          <img
                            src={outUrl}
                            alt="restored"
                            className="h-full w-full object-contain"
                            data-testid="restore-output-image"
                          />
                        ) : (
                          <div className="grid h-full place-items-center text-sm text-zinc-400" data-testid="restore-output-empty">
                            Restored preview will appear here
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
